import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X, Zap, Megaphone, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ToolLogo } from "@/components/shared";
import ConsentBanner from "@/components/ConsentBanner";

const NAV = [
  { label: "AI", to: "/ai" },
  { label: "AI Tools", to: "/tools" },
  { label: "AI Models", to: "/ai-models" },
  { label: "Comparisons", to: "/comparisons" },
  { label: "Deals", to: "/deals" },
  { label: "Gaming", to: "/gaming" },
  { label: "Tech", to: "/tech" },
  { label: "Reviews", to: "/reviews" },
  { label: "Guides", to: "/guides" },
];

const TICKER = [
  "GPT-5.2 ships with 400K context", "Claude Opus 4.5 tops coding benchmarks",
  "Gemini 3 Pro hits 2M token window", "DeepSeek R2 undercuts the market",
  "PS5 Pro adds PSSR AI upscaling", "RTX 5090 vs RX 8900 XTX — read the verdict",
  "100+ AI tools ranked in the KLYROO directory", "Best AI for coding in 2026",
];

function SearchOverlay({ onClose }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.search(q).then((r) => {
      setRes(r);
      const count = (r.tools?.length || 0) + (r.articles?.length || 0) + (r.comparisons?.length || 0) + (r.models?.length || 0);
      track.internalSearch(q.trim(), count);
    }), 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { onClose(); nav(path); };
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm p-4 sm:p-16 flex justify-center" onClick={onClose}>
      <div className="w-full max-w-2xl h-fit card-surface rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <Search size={18} className="text-klyroo-cyan" />
          <input autoFocus data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search tools, models, comparisons, articles…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-slate-500" />
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {!res && <p className="p-6 text-center text-sm text-slate-500 font-mono">Type at least 2 characters…</p>}
          {res && (
            <div className="divide-y divide-white/5">
              <ResultGroup title="Tools" items={res.tools} render={(t) => (
                <button key={t.slug} onClick={() => go(`/tools/${t.slug}`)} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-left">
                  <ToolLogo name={t.name} logo={t.logo} size={34} />
                  <div><div className="text-white text-sm font-semibold">{t.name}</div><div className="text-xs text-slate-500">{t.category}</div></div>
                </button>
              )} />
              <ResultGroup title="Comparisons" items={res.comparisons} render={(c) => (
                <button key={c.slug} onClick={() => go(`/comparisons/${c.slug}`)} className="w-full p-3 rounded-lg hover:bg-white/5 text-left text-sm text-white">{c.title}</button>
              )} />
              <ResultGroup title="Models" items={res.models} render={(m) => (
                <button key={m.slug} onClick={() => go(`/ai-models/${m.slug}`)} className="w-full p-3 rounded-lg hover:bg-white/5 text-left text-sm text-white">{m.name} <span className="text-slate-500">· {m.company}</span></button>
              )} />
              <ResultGroup title="Articles" items={res.articles} render={(a) => (
                <button key={a.slug} onClick={() => go(`/article/${a.slug}`)} className="w-full p-3 rounded-lg hover:bg-white/5 text-left text-sm text-white">{a.title}</button>
              )} />
              {res && !res.tools.length && !res.articles.length && !res.comparisons.length && !res.models.length &&
                <p className="p-6 text-center text-sm text-slate-500">No results for "{q}"</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function ResultGroup({ title, items, render }) {
  if (!items || !items.length) return null;
  return (<div className="py-2"><div className="overline px-3 py-1">{title}</div>{items.map(render)}</div>);
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [search, setSearch] = useState(false);
  const [ticker, setTicker] = useState(TICKER);
  const loc = useLocation();
  useEffect(() => { setMobile(false); }, [loc]);
  useEffect(() => {
    api.articles({ sort: "newest", limit: 10 }).then((d) => {
      const titles = (d.items || []).map((a) => a.title).filter(Boolean);
      if (titles.length) setTicker(titles);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", on); return () => window.removeEventListener("scroll", on);
  }, []);
  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");
  return (
    <>
      <div className="marquee-wrap overflow-hidden bg-klyroo-cyan/[0.06] border-b border-white/5 text-klyroo-cyan/90">
        <div className="marquee py-1.5 text-xs font-mono">
          {[...ticker, ...ticker].map((t, i) => <span key={i} className="mx-6 inline-flex items-center gap-2"><Zap size={11} /> {t}</span>)}
        </div>
      </div>
      <header className={`sticky top-0 z-50 ${scrolled ? "glass" : "border-b border-white/5"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-6 h-16">
          <Logo size={30} />
          <nav className="hidden lg:flex items-center gap-1 ml-2">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive(n.to) ? "text-klyroo-cyan" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="search-trigger" onClick={() => setSearch(true)} className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5"><Search size={18} /></button>
            <button className="lg:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-toggle">
              {mobile ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobile && (
          <div className="lg:hidden glass border-t border-white/5 px-4 py-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className="block py-2.5 text-slate-200 font-medium border-b border-white/5">{n.label}</Link>
            ))}
          </div>
        )}
      </header>
      {search && <SearchOverlay onClose={() => setSearch(false)} />}
    </>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 border-t border-white/10 bg-klyroo-surface/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-1">
          <Logo size={30} />
          <p className="text-sm text-slate-400 mt-4 leading-relaxed">AI. Gaming. Technology. Explained. A modern technology intelligence and discovery platform.</p>
        </div>
        <FooterCol title="Discover" links={[["AI Tools", "/tools"], ["AI Models", "/ai-models"], ["Comparisons", "/comparisons"], ["Deals", "/deals"], ["Reviews", "/reviews"]]} />
        <FooterCol title="Coverage" links={[["AI", "/ai"], ["Gaming", "/gaming"], ["Tech", "/tech"], ["Guides", "/guides"]]} />
        <FooterCol title="KLYROO" links={[["About", "/about"], ["Suggest", "/suggest"], ["Advertise", "/advertise"]]} />
        <FooterCol title="Legal" links={[["Privacy Policy", "/privacy"], ["Terms of Use", "/terms"], ["Disclaimer", "/disclaimer"], ["Affiliate & Advertising Disclosure", "/affiliate-disclosure"], ["Contact", "/contact"]]} />
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 font-mono">
          <span>© {new Date().getFullYear()} KLYROO. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <button data-testid="privacy-settings-trigger"
              onClick={() => window.dispatchEvent(new Event("klyroo:open-privacy"))}
              className="text-slate-500 hover:text-klyroo-cyan transition-colors">Privacy Settings</button>
            <span className="hidden sm:inline">Built as a technology intelligence platform · AI · Gaming · Technology</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
function FooterCol({ title, links }) {
  return (
    <div>
      <div className="overline mb-4">{title}</div>
      <ul className="space-y-2.5">
        {links.map(([l, to]) => <li key={to}><Link to={to} data-testid={`footer-link-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm text-slate-400 hover:text-klyroo-cyan transition-colors">{l}</Link></li>)}
      </ul>
    </div>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-klyroo-bg">
      <Header />
      <main className="flex-1">{children}</main>
      {pathname !== "/advertise" && <PartnerCTA />}
      <Footer />
      <ConsentBanner />
    </div>
  );
}

function PartnerCTA() {
  return (
    <section className="mt-24" data-testid="partner-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-klyroo-surface/60 p-8 sm:p-12">
          <div className="absolute inset-0 bg-gradient-to-br from-klyroo-cyan/10 via-transparent to-klyroo-violet/15 pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-klyroo-violet/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-klyroo-cyan/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-klyroo-cyan/10 border border-klyroo-cyan/20 text-klyroo-cyan text-[11px] font-mono uppercase tracking-widest">
                <Megaphone size={12} /> Partner with KLYROO
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-4">
                Advertise with <span className="bg-gradient-to-r from-klyroo-cyan to-klyroo-violet bg-clip-text text-transparent">KLYROO</span>
              </h2>
              <p className="text-slate-300 mt-3 text-base sm:text-lg">
                Reach a high-intent audience actively researching AI tools, models, hardware and games. Premium, clearly-labelled placements built to fit KLYROO's editorial experience.
              </p>
            </div>
            <Link to="/advertise" data-testid="partner-cta-link"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-klyroo-cyan to-klyroo-violet text-black font-semibold hover:opacity-90 transition-opacity">
              Start your campaign <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
